import { AxiosPromise } from "axios";
import SectionPanel from "components/panels/SectionPanel/SectionPanel";
import ValidatorContext from "formvalidation/FormContext";
import { useValidators } from "formvalidation/hooks";
import React, { useState } from "react";
import { Message } from "components/Atoms/Message";
import { Button } from "semantic-ui-react";
import { SectionTemplate } from "./SectionTemplate";
import { EditNewSection, newSectionFieldEditToSectionFieldEdit } from "./types";
import * as Sentry from "@sentry/browser";
import { InPageEditingProvider } from "./InPageEditingContext";

export type EditSectionWithRedirect = {
  redirectUrl: (itemId: number) => string
}

export type EditSectionWithOnSuccess = {
  onSuccess: (itemId: number) => void
}

export type EditSectionTemplateProps = {
  submit: () => AxiosPromise<server.dto.ApiResponse | server.dto.ApiResponseWrapper<any>>;
  sections: Array<EditNewSection>;
  noPanels?: boolean;
  saveButtonDisabled?: boolean;
  saveButtonLabel?: string;
} & (EditSectionWithRedirect | EditSectionWithOnSuccess)

export const EditSectionTemplate = (props: EditSectionTemplateProps) => {
  const { submit, sections, noPanels } = props;
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const validators = useValidators();

  const onSubmit = async () => {
    if (isLoading) return;

    setIsLoading(true);
    const validatorResult = await validators.validateAll();

    if (validatorResult.hasErrors) {
      if (!noPanels) {
        validatorResult.scrollToFirstError();
      }
      setIsLoading(false);
      return;
    }

    try {
      const response = await submit();

      setServerErrors(response.data.errors.map(e => e.message));

      let itemId: number = undefined;
      let isSuccessful = false

      if (!response.data.hasErrors) {
        isSuccessful = true;
        if ("data" in response.data && typeof response.data.data === "number") {
          itemId = response.data.data;
        }
        if ("data" in response.data && (typeof response.data.data === "object" && "fileId" in response.data.data)) {
          itemId = response.data.data.fileId;
        }
      }

      if (isSuccessful) {
        if ("redirectUrl" in props) {
          window.location.href = props.redirectUrl(itemId);
        } else if ("onSuccess" in props) {
          props.onSuccess(itemId);
        }
      } else {
        setIsLoading(false);
      }
    } catch (e) {
      console.error(e);
      Sentry.captureException(e);
      setIsLoading(false);
      setServerErrors([RESX.GeneralLabels.SomethingUnexpectedHappened]);
    }
  }

  const mappedSections = sections.map((section) => {
    if (section.type === "custom") {
      return section;
    }

    return {
      ...section,
      fields: section.fields.map((field) => ({
        ...field,
        isEmpty: false,
        render: field.renderEditNew,
        inPageEdit: field.inPageEdit ? newSectionFieldEditToSectionFieldEdit(field.inPageEdit) : undefined,
      })),
    }
  });

  const hasInPageEditableFields = mappedSections.some(section =>
    section.type !== "custom" && section.fields.some(field => field.inPageEdit !== undefined)
  );

  const Footer = () => <div className="flex-column gap">
    {serverErrors.length > 0 && <Message type="error">
      {serverErrors.map((error) => <div key={error}>{error}</div>)}
    </Message>}
    <div className="flex">
      <div className="grow1" />
      <Button
        primary
        type="submit"
        loading={isLoading}
        disabled={isLoading || props.saveButtonDisabled}
      >
        {props.saveButtonLabel || RESX.Buttons.btnSave}
      </Button>
    </div>
  </div>

  return <ValidatorContext settings={validators.validatorSettings}>
    <InPageEditingProvider
      enabled={hasInPageEditableFields}
      validate={validators.validateAll}
      onValueChanged={() => { }}
      onSuccessfulUpdate={() => Promise.resolve()}
    >
      {/* Turns off the browser's own validation. We validate with useValidators, and inputs like type="email" would otherwise show the browser's error bubble next to ours */}
      <form noValidate onSubmit={(e) => {
        e.preventDefault();

        // Stop propagation because we can have a form in a form (e.g. form > modal > form)
        e.stopPropagation();
        onSubmit();
      }}>
        <SectionTemplate
          isInPageEditable={hasInPageEditableFields}
          noPanels={props.noPanels}
          compact
          sections={[...mappedSections, {
            type: "custom",
            key: "submit",
            render: () =>
              props.noPanels
                ? <Footer key="saveButton" />
                : <SectionPanel.Panel key="saveButton">
                  <Footer />
                </SectionPanel.Panel>
          }]}
        />
      </form>
    </InPageEditingProvider>
  </ValidatorContext>
}